import { memo, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";

import { type ApolloCache, useMutation } from "util/graphql";
import { useForm } from "common/core/form";
import { TextInput } from "common/core/form/text";
import {
  isAriaInvalid,
  FormattedFieldError,
  FieldErrorMessage,
  defaultRequiredMessage,
} from "common/core/form/error";
import Button from "common/core/button";
import IllustrationModal from "common/modals/illustration_modal";
import { segmentTrack } from "util/segment";
import DigicertLogo from "assets/images/digicert/digicert_logo.svg";

import Styles from "./digicert_otp.module.scss";
import SendDigicertOtpMutation, {
  type SendDigicertOtp,
} from "./send_digicert_otp_mutation.graphql";
import SendDigicertOtpJournalExportMutation from "./send_digicert_otp_journal_export_mutation.graphql";

type FormValues = {
  otp: string;
};
type FormProps = {
  onSend: (values: FormValues) => Promise<unknown>;
};
type MeetingFormProps = {
  meetingId: string;
  onSuccess: (meetingId: string) => void;
  onCacheUpdate?: (
    cacheProxy: ApolloCache<unknown>,
    response: { data?: SendDigicertOtp | null },
  ) => void;
};
type JournalExportModalProps = {
  documentBundleId: string;
  onSuccess: () => void;
};

export const DIGICERT_OTP_TITLE_COPY = (
  <FormattedMessage
    id="2a892696-5be5-4bcc-bbaa-44744fb308c7"
    defaultMessage="Verify your DigiCert certificate"
  />
);

function getErrorCodeMessage(serverError: string | undefined) {
  switch (serverError) {
    case "invalid_otp":
      return (
        <FormattedMessage
          id="fd931a65-112f-4445-b7e6-889661711d32"
          defaultMessage="The code you entered is either invalid or expired. Please try again."
        />
      );
    case "unprocessable_entity":
      return (
        <FormattedMessage
          id="deabb197-8871-401d-a06c-207dccc4369c"
          defaultMessage="An unexpected error occurred. Please try again later."
        />
      );
    default:
      return (
        <FormattedMessage
          id="f24ff24c-4a06-45cd-a115-de8fe4349ffd"
          defaultMessage="Something went wrong. Please contact support."
        />
      );
  }
}

function OTPCodeForm({ onSend }: FormProps) {
  const intl = useIntl();
  const [errorMessage, setErrorMessage] = useState<ReturnType<typeof getErrorCodeMessage> | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { register, formState, handleSubmit } = useForm<FormValues>();
  const handleSend = (values: FormValues) => {
    setErrorMessage(null);
    setIsLoading(true);
    return onSend(values)
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        setErrorMessage(getErrorCodeMessage(error?.message));
        segmentTrack("SEND_DIGICERT_OTP_ERROR", error);
      });
  };
  return (
    <form onSubmit={handleSubmit(handleSend)}>
      <p className={Styles.explaination}>
        <FormattedMessage
          id="318046ec-fdbd-42f2-b8f1-d14c234fb661"
          defaultMessage="Enter the passcode from your authentication app to complete this transaction."
        />
      </p>

      <div className={Styles.certPassword}>
        <TextInput
          autoFocus
          aria-invalid={Boolean(errorMessage) || isAriaInvalid(formState.errors.otp)}
          {...register("otp", { required: defaultRequiredMessage(intl) })}
        />
        <Button type="submit" buttonColor="action" variant="primary" isLoading={isLoading}>
          <FormattedMessage id="59d3c212-bac3-4208-98db-ac5b82a368c8" defaultMessage="Submit" />
        </Button>
      </div>

      {errorMessage ? (
        <FieldErrorMessage inputName="otp" message={errorMessage} />
      ) : (
        <FormattedFieldError inputName="otp" error={formState.errors.otp} />
      )}
    </form>
  );
}

function MeetingOTPCodeForm({ meetingId, onSuccess, onCacheUpdate }: MeetingFormProps) {
  const sendDigicertOtpMutateFn = useMutation(SendDigicertOtpMutation);
  return (
    <OTPCodeForm
      onSend={({ otp }) => {
        return sendDigicertOtpMutateFn({
          variables: { input: { meetingId, digicertOtp: otp } },
          update: onCacheUpdate,
        }).then(() => onSuccess(meetingId));
      }}
    />
  );
}
const MemoizedMeetingOTPCodeForm = memo(MeetingOTPCodeForm);

function MeetingDigicertOTPModal(props: MeetingFormProps) {
  return (
    <IllustrationModal title={DIGICERT_OTP_TITLE_COPY} src={DigicertLogo}>
      <MemoizedMeetingOTPCodeForm {...props} />
    </IllustrationModal>
  );
}
const MemoizedMeetingDigicertOTPModal = memo(MeetingDigicertOTPModal);

function JournalExportDigicertOTPModal({ documentBundleId, onSuccess }: JournalExportModalProps) {
  const sendDigicertOtpJournalExportMutateFn = useMutation(SendDigicertOtpJournalExportMutation);
  return (
    <IllustrationModal title={DIGICERT_OTP_TITLE_COPY} src={DigicertLogo}>
      <OTPCodeForm
        onSend={({ otp }) => {
          return sendDigicertOtpJournalExportMutateFn({
            variables: { input: { documentBundleId, digicertOtp: otp } },
          }).then(() => onSuccess());
        }}
      />
    </IllustrationModal>
  );
}
const MemoizedJournalExportDigicertOTPModal = memo(JournalExportDigicertOTPModal);

export {
  MemoizedMeetingDigicertOTPModal as MeetingDigicertOTPModal,
  MemoizedMeetingOTPCodeForm as MeetingOTPCodeForm,
  MemoizedJournalExportDigicertOTPModal as JournalExportDigicertOTPModal,
};
